import { type FormEvent, useEffect, useRef, useState } from 'react';

import {
  PromptInput,
  PromptInputBody,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
} from '@/modules/ui/components/prompt-input/PromptInput';

interface ChatComposerProps {
  onSend: (text: string) => void;
  /** Finlay is answering: the next message can be written, and is sent once the reply is in. */
  busy: boolean;
  /** Text the box starts with: a message that wasn't sent, or a picked example. Remount with a `key` to change it. */
  defaultValue?: string;
  placeholder?: string;
}

/** Reads and clears the box; null when it holds only whitespace. */
function takeMessage(form: HTMLFormElement) {
  const field = form.elements.namedItem('message');
  if (!(field instanceof HTMLTextAreaElement)) return null;
  const text = field.value.trim();
  if (!text) return null;
  field.value = '';
  return text;
}

/** The message box: Enter sends, Shift+Enter starts a new line. */
export function ChatComposer({
  onSend,
  busy,
  defaultValue,
  placeholder = 'Message Finlay',
}: ChatComposerProps) {
  // Enter while Finlay answers keeps the message in the box and sends it once
  // the reply is in. It sends whatever the box holds then, so edits still count.
  const [queued, setQueued] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  const submit = (
    message: PromptInputMessage,
    event: FormEvent<HTMLFormElement>,
  ) => {
    if (!message.text?.trim()) return;
    if (busy) {
      formRef.current = event.currentTarget;
      setQueued(true);
      return;
    }
    const text = takeMessage(event.currentTarget);
    if (text) onSend(text);
  };

  useEffect(() => {
    if (busy || !queued) return;
    // The reply arriving is what sends the waiting message; clearing the flag
    // belongs to that send.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQueued(false);
    const text = formRef.current && takeMessage(formRef.current);
    if (text) onSend(text);
  }, [busy, queued, onSend]);

  return (
    <PromptInput
      autoFocus
      // Named for the view transition that carries the box from the home page or a new chat into the chat (index.css).
      className="focus-within:border-ring/40 focus-within:ring-ring/10 transition-[border-color,box-shadow] duration-150 [view-transition-name:chat-composer] focus-within:ring-[3px]"
      onSubmit={submit}
    >
      <PromptInputBody>
        <div className="flex items-end gap-2">
          <PromptInputTextarea
            aria-label="Message Finlay"
            className="peer max-h-48"
            defaultValue={defaultValue}
            placeholder={placeholder}
          />
          {/* Dimmed while the box is empty. No spinner while Finlay answers: the thread already shows it. */}
          <PromptInputSubmit
            aria-label="Send"
            className="mr-3 mb-3 size-8 shrink-0 transition-[background-color,opacity,transform] peer-placeholder-shown:opacity-50"
            disabled={busy}
          />
        </div>
        <p
          aria-live="polite"
          className="text-muted-foreground px-3 pb-2 text-xs empty:pb-0"
        >
          {queued && busy && 'Sends when Finlay has replied'}
        </p>
      </PromptInputBody>
    </PromptInput>
  );
}
